import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Select, Tag, Button, Space, Spin, Alert, Drawer, Descriptions, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconFlag, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getAdminReports,
  resolveAdminReport,
  dismissAdminReport,
  recordReportAppeal,
  IAdminReport,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter, enumColumnFilters, booleanColumnFilters, booleanOnFilter } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = { Pending: 'processing', Resolved: 'success', Dismissed: 'default' };
const STATUS_LABEL_KEYS: Record<string, string> = {
  Pending: TRANSLATIONS.ADMIN_REPORTS_STATUS_PENDING,
  Resolved: TRANSLATIONS.ADMIN_REPORTS_STATUS_RESOLVED,
  Dismissed: TRANSLATIONS.ADMIN_REPORTS_STATUS_DISMISSED,
};
const TARGET_TYPE_LABEL_KEYS: Record<string, string> = {
  Space: TRANSLATIONS.ADMIN_REPORTS_TARGET_TYPE_SPACE,
  User: TRANSLATIONS.ADMIN_REPORTS_TARGET_TYPE_USER,
  Message: TRANSLATIONS.ADMIN_REPORTS_TARGET_TYPE_MESSAGE,
  Photo: TRANSLATIONS.ADMIN_REPORTS_TARGET_TYPE_PHOTO,
};
const STATUS_OPTIONS = ['Pending', 'Resolved', 'Dismissed'];
const TARGET_TYPE_OPTIONS = ['Space', 'User', 'Message', 'Photo'];

// Photo's own TargetId is the photo's URL, not a numeric id (see
// ReportsController.CreateReport's own comment on why) -- "#<url>" would be
// unreadable, so this renders a link to actually look at the photo instead.
const renderTarget = (t: TFunction, report: IAdminReport): React.ReactNode =>
  report.targetType === 'Photo' ? (
    <>
      {translate(t, TARGET_TYPE_LABEL_KEYS.Photo)}{' '}
      <a href={report.targetId} target="_blank" rel="noopener noreferrer">
        {translate(t, TRANSLATIONS.ADMIN_REPORTS_VIEW_PHOTO_LINK)}
      </a>
    </>
  ) : (
    `${translate(t, TARGET_TYPE_LABEL_KEYS[report.targetType])} #${report.targetId}`
  );

// Queue of user reports (about spaces/users/messages), with status
// (pending/resolved/dismissed), the action taken, and a record of whether
// the outcome was appealed.
const ReportsSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [reports, setReports] = useState<IAdminReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [targetType, setTargetType] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminReport | null>(null);
  const [resolveTargetId, setResolveTargetId] = useState<string | undefined>(undefined);
  const [dismissTargetId, setDismissTargetId] = useState<string | undefined>(undefined);
  const [appealTargetId, setAppealTargetId] = useState<string | undefined>(undefined);

  const loadReports = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminReports(status, targetType)
      .then(setReports)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status, targetType]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const closeDrawerAndReload = () => {
    setDetail(null);
    loadReports();
  };

  const handleResolve = async (actionTaken: string) => {
    if (!resolveTargetId) return;
    try {
      await resolveAdminReport(resolveTargetId, actionTaken);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setResolveTargetId(undefined);
      closeDrawerAndReload();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleDismiss = async (reason: string) => {
    if (!dismissTargetId) return;
    try {
      await dismissAdminReport(dismissTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setDismissTargetId(undefined);
      closeDrawerAndReload();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleAppeal = async (appealNotes: string) => {
    if (!appealTargetId) return;
    try {
      await recordReportAppeal(appealTargetId, appealNotes);
      message.success(translate(t, TRANSLATIONS.ADMIN_REPORTS_APPEAL_RECORDED));
      setAppealTargetId(undefined);
      closeDrawerAndReload();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_REPORTS)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_REPORTS_STATUS_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS.map((value) => ({ value, label: translate(t, STATUS_LABEL_KEYS[value]) }))}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_REPORTS_TARGET_TYPE_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={targetType}
          onChange={setTargetType}
          options={TARGET_TYPE_OPTIONS.map((value) => ({ value, label: translate(t, TARGET_TYPE_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_REPORTS_LOAD_ERROR)} />
      ) : reports.length === 0 ? (
        <EmptyState
          icon={<IconFlag size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_REPORTS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_REPORTS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminReport>
          rowKey="id"
          dataSource={reports}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: renderTarget(t, record),
            subtitle: record.reporterName,
            badge: (
              <Tag color={STATUS_COLORS[record.status]} style={{ marginInlineEnd: 0 }}>
                {translate(t, STATUS_LABEL_KEYS[record.status])}
              </Tag>
            ),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_CREATED), value: new Date(record.createdAt).toLocaleDateString() },
              {
                label: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_APPEALED),
                value: record.wasAppealed ? <IconCheck size={16} color={token.colorSuccess} /> : '—',
              },
              { label: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_REASON), value: record.reason, wide: true },
            ],
            actions: (
              <Button size="small" onClick={() => setDetail(record)}>
                {translate(t, TRANSLATIONS.ADMIN_REPORTS_VIEW_BUTTON)}
              </Button>
            ),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_TARGET),
              key: 'target',
              filters: enumColumnFilters(TARGET_TYPE_OPTIONS, (value) => translate(t, TARGET_TYPE_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminReport) => record.targetType === value,
              render: (_: unknown, record: IAdminReport) => renderTarget(t, record),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_REPORTER),
              dataIndex: 'reporterName',
              key: 'reporterName',
              sorter: textSorter<IAdminReport>('reporterName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_REASON),
              dataIndex: 'reason',
              key: 'reason',
              ellipsis: true,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_STATUS),
              dataIndex: 'status',
              key: 'status',
              filters: enumColumnFilters(STATUS_OPTIONS, (value) => translate(t, STATUS_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminReport) => record.status === value,
              render: (value: string) => <Tag color={STATUS_COLORS[value]}>{translate(t, STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_APPEALED),
              dataIndex: 'wasAppealed',
              key: 'wasAppealed',
              filters: booleanColumnFilters(translate(t, TRANSLATIONS.ADMIN_COMMON_YES), translate(t, TRANSLATIONS.ADMIN_COMMON_NO)),
              onFilter: booleanOnFilter<IAdminReport>('wasAppealed'),
              render: (value: boolean) => (value ? <IconCheck size={16} color={token.colorSuccess} /> : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_CREATED),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminReport>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminReport) => (
                <Button size="small" onClick={() => setDetail(record)}>
                  {translate(t, TRANSLATIONS.ADMIN_REPORTS_VIEW_BUTTON)}
                </Button>
              ),
            },
          ]}
        />
      )}

      <Drawer title={translate(t, TRANSLATIONS.ADMIN_REPORTS_DETAIL_TITLE)} open={detail !== null} onClose={() => setDetail(null)} width={isMobile ? '100%' : 480}>
        {detail && (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_TARGET)}>
                {renderTarget(t, detail)}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_REPORTER)}>{detail.reporterName}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_REASON)}>{detail.reason}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_COL_STATUS)}>
                <Tag color={STATUS_COLORS[detail.status]}>{translate(t, STATUS_LABEL_KEYS[detail.status])}</Tag>
              </Descriptions.Item>
              {detail.actionTaken && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_ACTION_TAKEN_LABEL)}>{detail.actionTaken}</Descriptions.Item>
              )}
              {detail.wasAppealed && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_REPORTS_APPEAL_NOTES_LABEL)}>{detail.appealNotes}</Descriptions.Item>
              )}
            </Descriptions>

            <Space wrap>
              {detail.status === 'Pending' && (
                <>
                  <Button type="primary" onClick={() => setResolveTargetId(detail.id)}>
                    {translate(t, TRANSLATIONS.ADMIN_REPORTS_RESOLVE_BUTTON)}
                  </Button>
                  <Button danger onClick={() => setDismissTargetId(detail.id)}>
                    {translate(t, TRANSLATIONS.ADMIN_REPORTS_DISMISS_BUTTON)}
                  </Button>
                </>
              )}
              {!detail.wasAppealed && detail.status !== 'Pending' && (
                <Button onClick={() => setAppealTargetId(detail.id)}>{translate(t, TRANSLATIONS.ADMIN_REPORTS_APPEAL_BUTTON)}</Button>
              )}
            </Space>
          </>
        )}
      </Drawer>

      <AdminReasonModal
        open={resolveTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_REPORTS_RESOLVE_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_REPORTS_RESOLVE_BUTTON)}
        onCancel={() => setResolveTargetId(undefined)}
        onConfirm={handleResolve}
      />

      <AdminReasonModal
        open={dismissTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_REPORTS_DISMISS_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_REPORTS_DISMISS_BUTTON)}
        onCancel={() => setDismissTargetId(undefined)}
        onConfirm={handleDismiss}
      />

      <AdminReasonModal
        open={appealTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_REPORTS_APPEAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_REPORTS_APPEAL_BUTTON)}
        onCancel={() => setAppealTargetId(undefined)}
        onConfirm={handleAppeal}
      />
    </div>
  );
};

export default ReportsSection;
